import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import Send from '@mui/icons-material/Send';
import SendOutlined from '@mui/icons-material/SendOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import { SxProps, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import {
  JobStateEvent,
  OrderState,
  UserBulkAssignJobsChannel_Read,
  UserBulkCopyAssignmentsChannel_Read,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import {
  createRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BulkActionsBarButton } from '~components/BulkActionsBar/BulkActionsBar';
import { ComplianceWarningDialog } from '~components/CompliancePanel/ComplianceWarningDialog';
import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import ExportMenuButton, { ExportFormat } from '~components/Export/ExportMenuButton';
import { getDateRangePickerIconModeDisplayDate } from '~components/Filters/DateRangePicker';
import OrdersStatusFilter from '~components/Filters/OrdersStatusFilter';
import BulkEditJobDialog from '~components/Job/BulkEditJobDialog';
import QuickOrderDialog from '~components/Job/QuickOrderDialog';
import SendTextDialog from '~components/Job/SendTextDialog';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import OrderBySelector, {
  AvailableOrderOption,
} from '~components/OrderBySelector/OrderBySelector';
import { OrderSearchHighlightProvider } from '~components/OrderSearchHighlightContext/OrderSearchHighlightContext';
import { OrdersDispatchStorageKeys } from '~constants/dispatch';
import { FetchReason } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { trackJobAssignmentCancelled } from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import { makeCurrencyFormatter } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { createOrdersConfig } from '~hooks/useCSVImportExport/configs/ordersConfig';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import useDispatchNumberSortOptions from '~hooks/useDispatchNumberSortOptions';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import useBulkJobCardPdfs from '~hooks/useJobCardPdf/useBulkJobCardPdfs';
import useLoadDigestPdf from '~hooks/useLoadDigestPdf/useLoadDigestPdf';
import { LoadDigestLoadFilter, LoadDigestSelection } from '~hooks/useLoadDigests/models';
import buildDispatchSearchRequest from '~hooks/useOrders/buildDispatchSearchRequest';
import { Order } from '~hooks/useOrders/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import DownloadFilesDialog from '~pages/Approvals/Files/components/DownloadFilesDialog/DownloadFilesDialog';
import DateScopeBanner from '~pages/Dispatch/components/DateScopeBanner';
import DispatchUpdatesBanner from '~pages/Dispatch/components/DispatchUpdatesBanner';
import useDispatchOrdersRtuSmartRefresh from '~pages/Dispatch/hooks/useDispatchOrdersRtuSmartRefresh';
import useDispatchSafeAutoRefreshState from '~pages/Dispatch/hooks/useDispatchSafeAutoRefreshState';
import { useStores } from '~store';
import {
  ExpandedState,
  Filters as OrdersDispatchStoreFilters,
  OrderCardsExpandedStateStorageKey,
  OrdersSort,
} from '~store/OrdersDispatchStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import NoDnDDataAttributeMouseSensor from '~utils/dnd-kit/NoDnDDataAttributeMouseSensor';
import { useAwaitableFeatureFlag, useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AssignTypeMismatchDialog from './AssignTypeMismatchDialog';
import { BulkAssignJobsDialog } from './BulkAssignJobsDialog';
import BulkAssignMultipleJobsDialog from './BulkAssignMultipleJobsDialog';
import CloneOrderDialog from './CloneOrderDialog';
import { MODAL_DIALOG_ANIMATION_DURATION, OrdersPresentationMode } from './constants';
import CopyVendorAssignmentsDialog from './CopyVendorAssignmentsDialog';
import CreateOrderButton from './CreateOrderButton';
import { makeDispatchExportColumns } from './dispatchExportColumns';
import DownloadJobCardPdfsButton from './DownloadJobCardPdfsButton';
import DriverPanelAssigneeCard, {
  DriverPanelAssigneeCardPlacement,
  DriverPanelVendorTypeSubCard,
} from './DriverPanelAssigneeCard';
import { HEADER_HEIGHT, JOB_COLUMN_WIDTH } from './drivers/constants';
import EditDriverDrawer from './EditDriverDrawer';
import EditVendorDrawer from './EditVendorDrawer';
import OmniSearchBar from './filters/OmniSearchBar';
import OrdersDispatchFilters from './filters/OrdersDispatchFilters';
import { IDispatchViewComponent } from './IDispatchViewComponent';
import EmailLoadDigestDialog from './LoadDigest/EmailLoadDigestDialog';
import isLoadDigestRangeAllowed, {
  LOAD_DIGEST_MAX_RANGE_IN_DAYS,
} from './LoadDigest/isLoadDigestRangeAllowed';
import LoadDigestButton from './LoadDigest/LoadDigestButton';
import OrderForm from './OrderForm';
import { ExpandedStatesMenu } from './OrdersDispatchFiltersBar';
import OrdersGrid from './OrdersGrid';
import OrdersList from './OrdersList';
import OrdersPresentationMenu from './OrdersPresentationMenu';
import OrdersViewAppliedFiltersRow from './OrdersViewAppliedFiltersRow';
import OrdersViewBulkActionsBar, {
  useOrdersViewBulkActionsBar,
} from './OrdersViewBulkActionsBar';
import OrdersViewDriverPanel, {
  OrdersViewDriverPanelHandler,
} from './OrdersViewDriverPanel';
import RefreshOrdersListButton from './RefreshOrdersListButton';
import RejectOrderDialog from './RejectOrderDialog';
import { SendAllJobsForDayDialog } from './SendAllJobsForDayDialog';
import SharedDatesFilter from './SharedDatesFilter';
import SimulateOrderCTA from './SimulateOrderCTA';
import useOrderCreatedToaster from './useOrderCreatedToaster';

// Spec: suppress RTU-triggered refreshes for same-session mutations for 5 seconds.
const RECENT_MUTATION_TTL_IN_MS = 5000;

enum OrderActionDialog {
  BULK_ASSIGN_MULTIPLE = 'bulk_assign_multiple',
  CANCEL_ORDER = 'cancel_order',
  CLONE_ORDER = 'clone_order',
  EMAIL_LOAD_DIGEST = 'email_load_digest',
  TEXT_ALL_DRIVERS = 'text_all_drivers',
  REJECT_ORDER = 'reject_order',
}

type OrderActionDialogState = {
  dialog?: OrderActionDialog;
  isOpen: boolean;
  order?: Order;
};

type OrderFormState = {
  drawer: {
    isCopyAndEdit?: boolean;
    isMinimized: boolean;
    isOpen: boolean;
    keepMounted?: boolean;
    order?: Order;
  };
  confirmationDialog: {
    isOpen: boolean;
    order?: Order;
  };
};

const ordersFetchInitialState = {
  initialFetchComplete: false,
  isSilentRefresh: false,
  reason: FetchReason.INITIAL_FETCH,
  shouldFetch: false,
};

const ordersSortOptions: AvailableOrderOption<OrdersSort>[] = [
  {
    value: OrdersSort.DISPATCH_NUMBER_NAME,
    label: t('dispatch.dispatch_v2.orders_sort.dispatch_number'),
  },
  { value: OrdersSort.LOAD_AT, label: t('dispatch.dispatch_v2.orders_sort.load_at') },
  {
    value: OrdersSort.CREATED_AT,
    label: t('dispatch.dispatch_v2.orders_sort.created_at'),
  },
  {
    value: OrdersSort.COMPANY_LEGAL_NAME,
    label: t('dispatch.dispatch_v2.orders_sort.company_legal_name'),
  },
  {
    value: OrdersSort.ACCOUNT_NAME,
    label: t('dispatch.dispatch_v2.orders_sort.account_name'),
  },
  {
    value: OrdersSort.FRIENDLY_ORDER_ID,
    label: t('dispatch.dispatch_v2.orders_sort.friendly_order_id'),
  },
  {
    value: OrdersSort.TRUCK_COUNT,
    label: t('dispatch.dispatch_v2.orders_sort.truck_count'),
  },
];

const ORDER_DISPATCH_V3_GRID_ROW_HEIGHT_IN_PX = 47;
const LEFT_COLUMN_COLLAPSED_WIDTH_IN_PX = 42;
const FETCH_ORDERS_REQUIRED_FILTERS: Array<keyof OrdersDispatchStoreFilters> = ['search'];

const areAllRequiredFiltersInPlace = (currentFilters: OrdersDispatchStoreFilters) => {
  const isSearching = Boolean(currentFilters.search?.length);

  if (isSearching) {
    return true;
  }

  return FETCH_ORDERS_REQUIRED_FILTERS.every((filter) => filter in currentFilters);
};

const areDispatchFiltersActive = (currentFilters: OrdersDispatchStoreFilters) => {
  const hasSearch = Boolean(currentFilters.search?.trim());
  const hasDateRange = Boolean(currentFilters.startDate || currentFilters.endDate);

  const arrayFilters = [
    currentFilters.customerAccountIds,
    currentFilters.departmentIds,
    currentFilters.dispatchNumberIds,
    currentFilters.dispatchStatuses,
    currentFilters.driverIds,
    currentFilters.dropoffSites,
    currentFilters.equipmentTypes,
    currentFilters.foremen,
    currentFilters.jobStates,
    currentFilters.materialIds,
    currentFilters.orderStates,
    currentFilters.pickUpSites,
    currentFilters.projectIds,
    currentFilters.projectsExternalIds,
    currentFilters.refinedOrderStates,
    currentFilters.requesterIds,
    currentFilters.salespersonIds,
    currentFilters.siteTypes,
    currentFilters.vendorAccountIds,
  ];

  return (
    hasSearch ||
    hasDateRange ||
    arrayFilters.some((filters) => (filters?.length ?? 0) > 0)
  );
};

interface OrdersViewProviderProps extends IDispatchViewComponent {}

const expandedStateStorageKeyByPresentation = {
  cards: OrdersDispatchStorageKeys.EXPANDED_STATE,
  grid: OrdersDispatchStorageKeys.GRID_EXPANDED_STATE,
} satisfies Record<OrdersPresentationMode, OrderCardsExpandedStateStorageKey>;

const getOrderCardsDefaultExpandedState = (
  presentation: OrdersPresentationMode,
  isCollapsedByDefault: boolean,
) => {
  const gridRowsStartCollapsed = presentation === 'grid';

  return gridRowsStartCollapsed || isCollapsedByDefault
    ? ExpandedState.CLOSED
    : ExpandedState.OPEN;
};

const OrdersViewProvider = observer((props: OrdersViewProviderProps) => {
  const { ordersDispatchStore, userStore } = useStores();
  const [storedPresentation, setStoredPresentation] = useState<OrdersPresentationMode>(
    () =>
      localStorage.getItem(OrdersDispatchStorageKeys.PRESENTATION) === 'grid'
        ? 'grid'
        : 'cards',
  );

  const dispatchGridViewFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.dispatchGridView,
  });

  const simulateOrderFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.simulateOrder,
  });

  const dispatchSmartAutoRefreshFeatureFlagEnabled = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.dispatchSmartAutoRefresh,
  });

  // Load-level pricing edits re-rate jobs in an async worker; the board self-heals on tab/window
  // return only when smart-auto-refresh is on. That flag is off for the POC pricing companies, so
  // extend the (silent) self-heal to load_level_pricing too — same mechanism, just a wider gate.
  const loadLevelPricingFeatureFlagEnabled = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });

  const collapseDispatchOrderCardsByDefaultSettingEnabled =
    userStore.companySettings.isEnabled('collapse_dispatch_order_cards_by_default');

  const sortDispatchOrdersByDispatchNumberSettingEnabled =
    userStore.companySettings.isEnabled('sort_dispatch_orders_by_dispatch_number');

  const presentation = dispatchGridViewFeatureFlag.isFeatureFlagEnabled
    ? storedPresentation
    : 'cards';

  const switchExpandedStateToPresentation = useCallback(
    (nextPresentation: OrdersPresentationMode) => {
      ordersDispatchStore.switchOrderCardsExpandedState(
        expandedStateStorageKeyByPresentation[nextPresentation],
        getOrderCardsDefaultExpandedState(
          nextPresentation,
          collapseDispatchOrderCardsByDefaultSettingEnabled,
        ),
      );
    },
    [collapseDispatchOrderCardsByDefaultSettingEnabled, ordersDispatchStore],
  );

  const handlePresentationChange = useCallback(
    (nextPresentation: OrdersPresentationMode) => {
      if (nextPresentation !== storedPresentation) {
        AnalyticsController.track('dispatch_presentation_switched', {
          presentation: nextPresentation,
          previous_presentation: storedPresentation,
        });
      }

      switchExpandedStateToPresentation(nextPresentation);
      setStoredPresentation(nextPresentation);
      localStorage.setItem(OrdersDispatchStorageKeys.PRESENTATION, nextPresentation);
    },
    [storedPresentation, switchExpandedStateToPresentation],
  );

  useLayoutEffect(() => {
    switchExpandedStateToPresentation(presentation);
  }, [
    ordersDispatchStore.orderCardsExpandedStateStorageKey,
    presentation,
    switchExpandedStateToPresentation,
  ]);

  // Before child mount effects, so their tracked events carry the new presentation.
  useLayoutEffect(() => {
    if (!dispatchGridViewFeatureFlag.isReady) {
      return;
    }

    AnalyticsController.registerForSession({ presentation });

    return () => {
      AnalyticsController.unregisterForSession('presentation');
    };
  }, [dispatchGridViewFeatureFlag.isReady, presentation]);

  const isLoadingFeatureFlags =
    !dispatchGridViewFeatureFlag.isReady ||
    !dispatchSmartAutoRefreshFeatureFlagEnabled.isReady ||
    !loadLevelPricingFeatureFlagEnabled.isReady ||
    !simulateOrderFeatureFlag.isReady;

  if (isLoadingFeatureFlags) {
    return <CommonLoader />;
  }

  ordersDispatchStore.setDefaultOrdersSort(
    sortDispatchOrdersByDispatchNumberSettingEnabled
      ? { orderBy: OrdersSort.DISPATCH_NUMBER_NAME, sortBy: 'desc' }
      : { orderBy: OrdersSort.CREATED_AT, sortBy: 'desc' },
  );

  return (
    <OrdersView
      {...props}
      dispatchSmartAutoRefreshFeatureFlagEnabled={
        dispatchSmartAutoRefreshFeatureFlagEnabled.isFeatureFlagEnabled
      }
      loadLevelPricingEnabled={loadLevelPricingFeatureFlagEnabled.isFeatureFlagEnabled}
      onPresentationChange={
        dispatchGridViewFeatureFlag.isFeatureFlagEnabled
          ? handlePresentationChange
          : undefined
      }
      presentation={presentation}
      simulateOrderFeatureFlagEnabled={simulateOrderFeatureFlag.isFeatureFlagEnabled}
    />
  );
});

interface OrdersViewProps extends OrdersViewProviderProps {
  dispatchSmartAutoRefreshFeatureFlagEnabled?: boolean;
  loadLevelPricingEnabled?: boolean;
  onPresentationChange?: (presentation: OrdersPresentationMode) => void;
  presentation: OrdersPresentationMode;
  simulateOrderFeatureFlagEnabled?: boolean;
}

const OrdersView = observer(
  ({
    dispatchSmartAutoRefreshFeatureFlagEnabled,
    loadLevelPricingEnabled,
    onPresentationChange,
    presentation,
    simulateOrderFeatureFlagEnabled,
    viewSelector,
  }: OrdersViewProps) => {
    const theme = useTheme();
    const OrdersPresentation = presentation === 'grid' ? OrdersGrid : OrdersList;
    const [columnsMenuContainer, setColumnsMenuContainer] = useState<HTMLElement | null>(
      null,
    );

    const { ordersDispatchStore, userStore, toasterStore } = useStores();
    const isCsvOrderImportFeatureFlagOn = useFeatureFlag({
      featureFlagKey: FeatureFlags.csvOrderImport,
    });
    const orderSearchHighlightTerm = ordersDispatchStore.filters.search;
    const {
      acceptOrder,
      bulkAssignJobs,
      bulkCancelJobs,
      bulkUnassignJobs,
      bulkCopyAssignments,
      bulkSendJobs,
      bulkSendJobsByDay,
      cancelDragAssignment,
      cancelOrder,
      cancelTypeMismatchAssignment,
      completeOrder,
      confirmDragAssignment,
      confirmTypeMismatchAssignment,
      isDragOverrideSubmitting,
      isTypeMismatchSubmitting,
      createJobFromOrder,
      fetchAllJobsForExport,
      getBulkSendJobsByDayCount,
      getCompanyOrdersByCompanyId,
      getJobsByOrder,
      handleAssigneeDragCancel,
      handleAssigneeDragEnd,
      handleAssigneeDragStart,
      pendingDragAssignment,
      pendingTypeMismatchAssignment,
      subscribeToBulkAssignJobsRTU,
      subscribeToBulkCopyAssignmentsRTU,
      subscribeToJobsRTU,
      subscribeToJobUnassignmentRTU,
      subscribeToOrdersRTU,
    } = useOrdersDispatch();
    const {
      acceptOrder: acceptOrderEvent,
      holdOrder,
      requestOrder,
      startOrder,
      weatherHoldOrder,
    } = useOrders();
    const { generateJobCardPdfsForDispatch, generateJobCardPdfsForJobs } =
      useBulkJobCardPdfs();
    const { viewForDispatch, viewForJobs } = useLoadDigestPdf();
    const [loadDigestEmailSelection, setLoadDigestEmailSelection] =
      useState<LoadDigestSelection | null>(null);
    const { showOrderCreatedToaster } = useOrderCreatedToaster();
    const { singular } = useDispatchNumberLabel();
    const relabelSortOptions = useDispatchNumberSortOptions();
    const sortOptions = useMemo(
      () => relabelSortOptions(ordersSortOptions, OrdersSort.DISPATCH_NUMBER_NAME),
      [relabelSortOptions],
    );
    const { isViewOnlyUser } = userStore;
    const { currency } = userStore.userCompany;
    const dispatchExportColumns = useMemo(
      () =>
        makeDispatchExportColumns(
          makeCurrencyFormatter(currency),
          singular('export.columns.dispatch_number'),
        ),
      [currency, singular],
    );
    const makeExportFilename = useCallback(
      (extension: ExportFormat) => {
        const dateForFilename = dayjs
          .tz(ordersDispatchStore.filters.startDate)
          .format('YYYY-MM-DD');
        return `${userStore.userCompany.legalName}_${dateForFilename}.${extension}`;
      },
      [ordersDispatchStore.filters.startDate, userStore.userCompany.legalName],
    );
    const dispatchExportFilterDateLabel = useMemo(
      () =>
        getDateRangePickerIconModeDisplayDate(
          ordersDispatchStore.filters.startDate
            ? dayjs.tz(ordersDispatchStore.filters.startDate)
            : null,
          ordersDispatchStore.filters.endDate
            ? dayjs.tz(ordersDispatchStore.filters.endDate)
            : null,
          true,
          true,
        ),
      [ordersDispatchStore.filters.startDate, ordersDispatchStore.filters.endDate],
    );
    const autoExtendedFromRange = ordersDispatchStore.autoExtendedFromRange;
    const autoExtendedDateLabel = useMemo(
      () =>
        getDateRangePickerIconModeDisplayDate(
          autoExtendedFromRange?.startDate
            ? dayjs.tz(autoExtendedFromRange.startDate)
            : null,
          autoExtendedFromRange?.endDate ? dayjs.tz(autoExtendedFromRange.endDate) : null,
          true,
          true,
        ),
      [autoExtendedFromRange?.startDate, autoExtendedFromRange?.endDate],
    );
    // Tenet 1 (TRE-15949): the search matched inside the selected range but there are also
    // matches on other dates. Mutually exclusive with autoExtendedFromRange (which only fires
    // when the in-range fetch is empty), so the two banner variants never render together.
    const hasResultsOutsideDateRange = ordersDispatchStore.hasResultsOutsideDateRange;
    const selectedDateRangeLabel = useMemo(
      () =>
        getDateRangePickerIconModeDisplayDate(
          ordersDispatchStore.filters.startDate
            ? dayjs.tz(ordersDispatchStore.filters.startDate)
            : null,
          ordersDispatchStore.filters.endDate
            ? dayjs.tz(ordersDispatchStore.filters.endDate)
            : null,
          true,
          true,
        ),
      [ordersDispatchStore.filters.startDate, ordersDispatchStore.filters.endDate],
    );
    const [ordersFetchState, setOrdersFetchState] = useState({
      ...ordersFetchInitialState,
    });
    const inFlightOrdersFetchKeyRef = useRef<string | null>(null);
    const pendingRefreshRequestRef = useRef<{
      reason: FetchReason;
      resolve: (success: boolean) => void;
    } | null>(null);
    const onOrdersFetchCompletedRef = useRef<
      ((outcome: { reason: FetchReason; success: boolean }) => void) | null
    >(null);
    const [orderActionDialog, setOrderActionDialog] = useState<OrderActionDialogState>({
      isOpen: false,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [summaryCountsRefreshToken, setSummaryCountsRefreshToken] = useState(0);
    const updatesBannerRef = useRef<HTMLDivElement | null>(null);
    const [updatesBannerHeight, setUpdatesBannerHeight] = useState(0);
    const dateScopeBannerRef = useRef<HTMLDivElement | null>(null);
    const [dateScopeBannerHeight, setDateScopeBannerHeight] = useState(0);
    const recentMutationTimeoutsRef = useRef<Map<string, number>>(new Map());
    const recentlyMutatedOrderIdsRef = useRef<Set<string>>(new Set());

    const [isSendAllJobsDialogOpen, setIsSendAllJobsDialogOpen] = useState(false);
    const [isSendingSelectedJobs, setIsSendingSelectedJobs] = useState(false);
    const isSendingSelectedJobsRef = useRef(false);

    const { treatments, isReady } = useSplitTreatments({
      names: [FeatureFlags.llmQuickOrder, FeatureFlags.bulkSendDayButton],
    });
    const llmQuickOrderFeatureFlag = treatments[FeatureFlags.llmQuickOrder];
    const isLlmQuickOrderFeatureFlagOn =
      isReady && llmQuickOrderFeatureFlag.treatment === 'on';
    const bulkSendDayButtonFeatureFlag = treatments[FeatureFlags.bulkSendDayButton];
    const isBulkSendDayButtonFeatureFlagOn =
      isReady && bulkSendDayButtonFeatureFlag.treatment === 'on';

    const [isQuickOrderDialogOpen, setIsQuickOrderDialogOpen] = useState(false);

    const [downloadFilesDialogState, setDownloadFilesDialogState] = useState<{
      isOpen: boolean;
      jobIds: string[];
    }>({ isOpen: false, jobIds: [] });

    const [orderFormState, setOrderFormState] = useState<OrderFormState>({
      drawer: { isMinimized: false, isOpen: false },
      confirmationDialog: { isOpen: false },
    });

    const userPermissions = userStore.getPermissions();
    const canEditDrivers = userPermissions.canEditUser;
    const canImportOrdersFromCsv =
      isCsvOrderImportFeatureFlagOn && userPermissions.canCreateImportedOrder;
    const canEditVendors = userPermissions.canEditAccount;
    const driverPanelRef = useRef<OrdersViewDriverPanelHandler>(null);
    const [driverToEdit, setDriverToEdit] = useState<DriverTypeahead>();
    const [vendorToEdit, setVendorToEdit] = useState<AccountCapacityTypeahead>();

    const bulkActions = useOrdersViewBulkActionsBar(
      ordersDispatchStore.orderJobsSnapshot,
    );
    const isBulkActionsBarVisible = bulkActions.jobsCount > 0;
    const { checkedJobIds } = bulkActions;
    const generateJobCardPdfsForCheckedJobs = useCallback(
      (recipientType: JobCardPdfRecipientType) =>
        generateJobCardPdfsForJobs(checkedJobIds, recipientType),
      [checkedJobIds, generateJobCardPdfsForJobs],
    );
    const viewLoadDigestForCheckedJobs = useCallback(
      (loadFilter: LoadDigestLoadFilter) => viewForJobs(checkedJobIds, loadFilter),
      [checkedJobIds, viewForJobs],
    );
    const hasChildCompanies = userStore.userCompanies.length > 1;

    // Set only when every checked job belongs to the same order — used to look
    // up that order's own editable flag below, independent of company scoping.
    const singleCheckedOrderId = useMemo(() => {
      const hasOnlyOneOrderWithCheckedJobs =
        bulkActions.jobsWithEditableNotes.uniqueOrdersHavingCheckedJobs.size === 1;
      if (!hasOnlyOneOrderWithCheckedJobs) return undefined;

      const [checkedOrderId] =
        bulkActions.jobsWithEditableNotes.uniqueOrdersHavingCheckedJobs.keys();

      return checkedOrderId;
    }, [bulkActions.jobsWithEditableNotes]);

    // An order's own materials belong to its own company, not necessarily the
    // acting user's, so bulk-editing material is only safe when every checked
    // job's order resolves to the same company — once that holds, any one of
    // those orders' ids is valid for the materials-scoped typeahead call.
    const materialsOrderId = useMemo(() => {
      const checkedOrderIds = [
        ...bulkActions.jobsWithEditableNotes.uniqueOrdersHavingCheckedJobs,
      ];
      const checkedOrders = checkedOrderIds.map((checkedOrderId) =>
        ordersDispatchStore.orders.find((order) => order.id === checkedOrderId),
      );

      const everyCheckedOrderSharesOneCompany =
        checkedOrders.every((order) => order?.company?.id) &&
        new Set(checkedOrders.map((order) => order?.company?.id)).size === 1;

      return everyCheckedOrderSharesOneCompany ? checkedOrderIds[0] : undefined;
    }, [bulkActions.jobsWithEditableNotes, ordersDispatchStore.orders]);

    const shouldLockNonEditableFieldsInBulkEditJobsDialog = useMemo(() => {
      if (!singleCheckedOrderId) return false;

      const order = ordersDispatchStore.orders.find(
        (order) => order.id === singleCheckedOrderId,
      );

      return order ? !order.editable : false;
    }, [singleCheckedOrderId, ordersDispatchStore.orders]);

    const companyId = getEffectiveUserCompanyId(userStore);
    const isReadyToFetchOrders = areAllRequiredFiltersInPlace(
      ordersDispatchStore.filters,
    );

    const currentFilters = JSON.stringify(
      ordersDispatchStore.filters,
      // this is to presever end & start dates when "All Dates" filters is selected, as
      // we want to keep track of the whole set of filters applied
      (_, value) => value ?? null,
    );
    const previousFilters = usePrevious(currentFilters);

    const isSingleDaySelected = useMemo(() => {
      const { startDate, endDate } = ordersDispatchStore.filters;

      if (!startDate || !endDate) {
        return false;
      }

      return dayjs(startDate).isSame(dayjs(endDate), 'day');
    }, [ordersDispatchStore.filters.startDate, ordersDispatchStore.filters.endDate]);

    const isLoadDigestRangeAllowedForFilters = useMemo(
      () =>
        isLoadDigestRangeAllowed(
          ordersDispatchStore.filters.startDate,
          ordersDispatchStore.filters.endDate,
        ),
      [ordersDispatchStore.filters.startDate, ordersDispatchStore.filters.endDate],
    );

    const previousOrdersSort = usePrevious(ordersDispatchStore.ordersSort);
    const hasActiveFilters = areDispatchFiltersActive(ordersDispatchStore.filters);
    const isAllDatesSelected =
      !ordersDispatchStore.filters.startDate && !ordersDispatchStore.filters.endDate;

    const autoExtendedBannerVisible = ordersDispatchStore.isDateScopeAutoExtended;
    const isDateScopeBannerVisible =
      autoExtendedBannerVisible || hasResultsOutsideDateRange;

    const triggerSummaryCountsRefresh = useCallback(() => {
      setSummaryCountsRefreshToken((token) => token + 1);
    }, []);

    const markOrderAsRecentlyMutated = useCallback((orderId: string) => {
      const existingTimeout = recentMutationTimeoutsRef.current.get(orderId);

      if (existingTimeout) {
        window.clearTimeout(existingTimeout);
      }

      recentlyMutatedOrderIdsRef.current.add(orderId);

      const timeoutId = window.setTimeout(() => {
        recentlyMutatedOrderIdsRef.current.delete(orderId);
        recentMutationTimeoutsRef.current.delete(orderId);
      }, RECENT_MUTATION_TTL_IN_MS);

      recentMutationTimeoutsRef.current.set(orderId, timeoutId);
    }, []);

    const hasBulkDialogsOpen = Object.values(bulkActions.dialogsState).some(
      (dialog) => dialog.isOpen,
    );

    const hasBlockingDialogOpen =
      orderActionDialog.isOpen ||
      orderFormState.drawer.isOpen ||
      orderFormState.confirmationDialog.isOpen ||
      hasBulkDialogsOpen ||
      isQuickOrderDialogOpen ||
      isSendAllJobsDialogOpen ||
      downloadFilesDialogState.isOpen ||
      loadDigestEmailSelection !== null;

    const hasJobMutationsInFlight =
      Array.from(ordersDispatchStore.isAcceptingOrRejectingJobs.values()).some(Boolean) ||
      Array.from(ordersDispatchStore.isUpdatingOrderJob.values()).some(Boolean) ||
      Array.from(ordersDispatchStore.isSendingJobs.values()).some(Boolean);

    const hasInFlightMutation =
      ordersDispatchStore.isLoadingOrders ||
      ordersDispatchStore.isSavingOrder ||
      ordersDispatchStore.isCancellingOrder ||
      ordersDispatchStore.isCompletingOrder ||
      ordersDispatchStore.isCloningOrder ||
      ordersDispatchStore.isCreatingJobFromOrder ||
      Boolean(ordersDispatchStore.acceptingOrRejectingOrderId) ||
      hasJobMutationsInFlight;

    const { isSafeToAutoApply } = useDispatchSafeAutoRefreshState({
      hasBlockingDialogOpen,
      hasInFlightMutation,
      isAllDatesSelected,
      isBulkSelectionActive: bulkActions.jobsCount > 0,
      isDragging,
      isFeatureFlagEnabled: Boolean(dispatchSmartAutoRefreshFeatureFlagEnabled),
      preventUpsertOrder: ordersDispatchStore.preventUpsertOrder,
    });

    const openOrderActionDialog = useCallback(
      (dialog: OrderActionDialog, order?: Order) => {
        setOrderActionDialog((state) => ({ ...state, isOpen: true, dialog, order }));
      },
      [],
    );

    const closeOrderActionDialog = useCallback(() => {
      setOrderActionDialog((state) => ({ ...state, isOpen: false, order: undefined }));
      bulkActions.closeDialog('textAllDrivers');
    }, []);

    const handleDownloadFilesClick = useCallback(
      (order: Order) => {
        const orderJobs = ordersDispatchStore.orderJobs.get(order.id) ?? [];
        const jobIds = orderJobs.map((job) => job.id);

        if (jobIds.length === 0) {
          return;
        }

        setDownloadFilesDialogState({ isOpen: true, jobIds });
      },
      [ordersDispatchStore.orderJobs],
    );

    const dismissCurrentDialogAndOpenDrawerForOrder = useCallback(
      (order: Order, isCopyAndEdit: boolean = false) => {
        setOrderFormState((state) => ({
          ...state,
          drawer: { isMinimized: false, isOpen: false },
        }));

        setTimeout(() => {
          setOrderFormState((state) => ({
            ...state,
            drawer: { isCopyAndEdit, isMinimized: false, isOpen: true, order },
          }));
        }, MODAL_DIALOG_ANIMATION_DURATION);
      },
      [],
    );

    const handleEditOrder = useCallback(
      (order: Order) => {
        if (orderFormState.drawer.isMinimized) {
          // drawer is for editing an order
          if (orderFormState.drawer.order?.id) {
            // same order -> open drawer right away
            if (orderFormState.drawer.order.id === order.id) {
              setOrderFormState((state) => ({
                ...state,
                drawer: {
                  ...state.drawer,
                  isCopyAndEdit: false,
                  isMinimized: false,
                },
              }));
            }

            // different order -> ask for confirmation
            else {
              setOrderFormState((state) => ({
                ...state,
                confirmationDialog: { isOpen: true, order },
              }));
            }
          }

          // drawer is for creating an order -> ask for confirmation
          else {
            setOrderFormState((state) => ({
              ...state,
              confirmationDialog: { isOpen: true, order },
            }));
          }
        } else {
          setOrderFormState((state) => ({
            ...state,
            drawer: { isCopyAndEdit: false, isMinimized: false, isOpen: true, order },
          }));
        }
      },
      [orderFormState],
    );

    const deepLinkedOrderId = ordersDispatchStore.deepLinkedOrder?.id;
    const getJobsByOrderRef = useRef(getJobsByOrder);
    getJobsByOrderRef.current = getJobsByOrder;

    useEffect(() => {
      if (
        !deepLinkedOrderId ||
        ordersDispatchStore.isLoadingOrderJobs.get(deepLinkedOrderId) === true
      ) {
        return;
      }

      const controller = new AbortController();
      void getJobsByOrderRef
        .current({
          orderId: deepLinkedOrderId,
          companyId,
          signal: controller.signal,
        })
        .catch(() => {
          // Error is handled by the hook
        });

      return () => controller.abort();
    }, [companyId, deepLinkedOrderId, ordersDispatchStore]);

    const handleAcceptOrder = useCallback(
      async (orderId: string) => {
        try {
          const acceptedOrder = await acceptOrder(orderId);

          if (acceptedOrder) {
            toasterStore.push(
              alert(
                t('dispatch.order.updated', { name: acceptedOrder.orderId }),
                AlertTypes.success,
              ),
            );
          }
        } catch {
          // Error is handled by the hook
        }
      },
      [acceptOrder, toasterStore],
    );

    const handleAllCardsExpandedStateChange = useCallback((expandedState: string) => {
      ordersDispatchStore.setOrderCardsExpandedState({
        allCards: expandedState,
        cardsByOrder: new Map(),
      });
    }, []);

    const handleBulkAssignJobsUpdateReceived = useCallback(
      (data: UserBulkAssignJobsChannel_Read) => {
        const hasSuccessfulUpdates = (data.assigned_job_friendly_ids?.length ?? 0) > 0;
        const hasFailedUpdates = (data.failed_job_friendly_ids?.length ?? 0) > 0;
        const failedJobIds = data.failed_job_friendly_ids
          ?.map((jobId) => `TRD-${jobId}`)
          .join(', ');

        if (hasSuccessfulUpdates && hasFailedUpdates) {
          toasterStore.push(
            alert(
              t('dispatch.order.bulk_jobs_sent', {
                success_num: data.assigned_job_friendly_ids?.length,
                failed_num: data.failed_job_friendly_ids?.length,
                failed_ids: `${failedJobIds}`,
              }),
              AlertTypes.error,
            ),
          );
        } else if (hasFailedUpdates) {
          toasterStore.push(
            alert(
              t('dispatch.order.bulk_jobs_sent_all_updates_failed', {
                failed_num: data.failed_job_friendly_ids?.length,
                failed_ids: `${failedJobIds}`,
              }),
              AlertTypes.error,
            ),
          );
        } else {
          toasterStore.push(
            alert(
              t('dispatch.order.bulk_jobs_sent_all_updates_successful', {
                success_num: data.assigned_job_friendly_ids?.length,
              }),
              AlertTypes.success,
            ),
          );
        }
      },
      [],
    );

    const handleBulkCopyAssignmentsUpdateReceived = useCallback(
      (data: UserBulkCopyAssignmentsChannel_Read) => {
        toasterStore.push(
          alert(
            t('dispatch.order.bulk_jobs_sent', {
              success_num: data.copied_job_friendly_ids?.length,
              failed_num: data.failed_job_friendly_ids?.length,
              failed_ids: ` ${data.failed_job_friendly_ids?.map((v) => `TRD-${v}`).join(', ')}`,
            }),
            (data.failed_job_friendly_ids?.length ?? 0) > 0
              ? AlertTypes.error
              : AlertTypes.success,
          ),
        );
      },
      [],
    );

    const sendAllJobsDate = isSingleDaySelected
      ? ordersDispatchStore.filters.startDate
      : undefined;
    // The count and the send share one filters snapshot: a filter change while the dialog is
    // open (e.g. a debounced search committing) re-creates the count callback, which makes the
    // dialog discard its count and refetch before confirm re-enables.
    const sendAllJobsFilters = ordersDispatchStore.filters;

    const fetchSendAllJobsCount = useCallback(() => {
      if (!sendAllJobsDate) {
        return Promise.resolve(0);
      }

      return getBulkSendJobsByDayCount(sendAllJobsDate, sendAllJobsFilters, companyId);
    }, [companyId, getBulkSendJobsByDayCount, sendAllJobsDate, sendAllJobsFilters]);

    const handleSendSelectedJobs = async () => {
      if (isSendingSelectedJobsRef.current) {
        return;
      }

      isSendingSelectedJobsRef.current = true;
      setIsSendingSelectedJobs(true);

      try {
        await bulkSendJobs(bulkActions.sendableJobs, companyId);
        bulkActions.clearAllCheckedOrdersAndJobs();
      } catch {
        // Error is handled by the hook
      } finally {
        isSendingSelectedJobsRef.current = false;
        setIsSendingSelectedJobs(false);
      }
    };

    const handleBulkSendJobsForDay = useCallback(async () => {
      if (!sendAllJobsDate) {
        return;
      }

      await bulkSendJobsByDay(sendAllJobsDate, sendAllJobsFilters, companyId);
      AnalyticsController.track('send_all_jobs_submitted', { view: 'orders' });
      setIsSendAllJobsDialogOpen(false);
    }, [bulkSendJobsByDay, companyId, sendAllJobsDate, sendAllJobsFilters]);

    const handleOrderCloned = useCallback(
      (order: Order) => {
        if (dispatchSmartAutoRefreshFeatureFlagEnabled) {
          ordersDispatchStore.setDeepLinkedOrder(order, { source: 'created' });
          toasterStore.push(
            alert(
              t('dispatch.order.created', { name: order.orderId }),
              AlertTypes.success,
            ),
          );
        } else {
          showOrderCreatedToaster(order);
        }
        closeOrderActionDialog();
      },
      [
        dispatchSmartAutoRefreshFeatureFlagEnabled,
        ordersDispatchStore,
        toasterStore,
        showOrderCreatedToaster,
        closeOrderActionDialog,
      ],
    );

    const handleCompleteOrder = useCallback(
      async (orderId: string) => {
        try {
          const completedOrder = await completeOrder(orderId);

          if (completedOrder) {
            toasterStore.push(
              alert(
                t('dispatch.order.completed', { name: completedOrder.orderId }),
                AlertTypes.success,
              ),
            );
          }
        } catch {
          // Error is handled by the hook
        }
      },
      [completeOrder, toasterStore],
    );

    const handleReturnToAutoExtendedDates = useCallback(() => {
      const range = ordersDispatchStore.autoExtendedFromRange;

      if (range) {
        ordersDispatchStore.markDateScopeUserChosen();
        ordersDispatchStore.setFilters(
          { startDate: range.startDate, endDate: range.endDate },
          true,
        );
        ordersDispatchStore.setAutoExtendedFromRange(null);
        AnalyticsController.track('dispatch_date_scope_restored');
      }
    }, [ordersDispatchStore]);

    // Tenet 1 (TRE-15949): clear the selected dates to show results from all dates, then drop the
    // scoped-to-range banner (resetOrders also clears it on the refetch, but null it eagerly).
    const handleShowResultsFromAllDates = useCallback(() => {
      ordersDispatchStore.markDateScopeUserChosen();
      ordersDispatchStore.setFilters({ startDate: undefined, endDate: undefined }, true);
      ordersDispatchStore.setHasResultsOutsideDateRange(false);
    }, [ordersDispatchStore]);

    const handleCreateJobFromOrder = useCallback(
      async (orderId: string) => {
        try {
          const createdJob = await createJobFromOrder(orderId);

          toasterStore.push(
            alert(
              t('dispatch.order.added_job', { name: createdJob.jobId }),
              AlertTypes.success,
            ),
          );
        } catch {
          // Error is handled by the hook
        }
      },
      [createJobFromOrder, toasterStore],
    );

    // The silent self-heal refetch is enabled by smart-auto-refresh OR load-level pricing, so LLP
    // companies recover from a missed live push (background tab during an async re-rate) without the
    // smart-refresh flag — and still silently, never the flashy full reset.
    const selfHealRefreshEnabled =
      Boolean(dispatchSmartAutoRefreshFeatureFlagEnabled) ||
      Boolean(loadLevelPricingEnabled);

    const requestOrdersRefresh = useCallback(
      (reason: FetchReason, options?: { silent?: boolean }) => {
        const isSilentRefresh = Boolean(options?.silent) && selfHealRefreshEnabled;

        if (!isSilentRefresh) {
          ordersDispatchStore.resetOrders(ordersDispatchStore.filters);
        }

        setOrdersFetchState((state) => ({
          ...state,
          isSilentRefresh,
          shouldFetch: true,
          reason,
        }));

        return new Promise<boolean>((resolve) => {
          pendingRefreshRequestRef.current = { reason, resolve };
        });
      },
      [selfHealRefreshEnabled, ordersDispatchStore, ordersDispatchStore.filters],
    );

    const handleRefreshOrdersClick = useCallback(() => {
      void requestOrdersRefresh(FetchReason.REFRESH);
    }, [requestOrdersRefresh]);

    const ordersImportConfig = useMemo(
      () => createOrdersConfig(singular('order.form.dispatch_number_full')),
      [singular],
    );
    const csvImportModal = useCSVModal({ onImportComplete: handleRefreshOrdersClick });

    const performOrdersFetch = useCallback(
      async (reason: FetchReason, isSilentRefresh: boolean = false) => {
        if (ordersDispatchStore.deepLinkedOrder) {
          // do not fetch when filters have been changed due to deep-linked order. in this case
          // we update the filters for UI/UX purpose only, but we use the API response directly
          // to show the order.
          setOrdersFetchState((state) => ({
            ...state,
            isSilentRefresh: false,
            shouldFetch: false,
            initialFetchComplete: true,
            reason,
          }));
          onOrdersFetchCompletedRef.current?.({ reason, success: true });
          const pendingRefreshRequest = pendingRefreshRequestRef.current;
          if (pendingRefreshRequest && pendingRefreshRequest.reason === reason) {
            pendingRefreshRequest.resolve(true);
            pendingRefreshRequestRef.current = null;
          }
          return true;
        }

        if (
          [FetchReason.FILTERS_CHANGED, FetchReason.ORDER_BY_CHANGED].includes(reason)
        ) {
          handleAllCardsExpandedStateChange(
            ordersDispatchStore.orderCardsDefaultExpandedState,
          );
        }

        let success = true;

        try {
          if (companyId) {
            await getCompanyOrdersByCompanyId({
              companyId,
              forceFirstPage: isSilentRefresh,
              suppressLoadingState: isSilentRefresh,
            });
          } else {
            success = false;
          }
        } catch {
          success = false;
        } finally {
          setOrdersFetchState((state) => ({
            ...state,
            isSilentRefresh: false,
            shouldFetch: false,
            initialFetchComplete: true,
            reason,
          }));

          onOrdersFetchCompletedRef.current?.({ reason, success });

          const pendingRefreshRequest = pendingRefreshRequestRef.current;
          if (pendingRefreshRequest && pendingRefreshRequest.reason === reason) {
            pendingRefreshRequest.resolve(success);
            pendingRefreshRequestRef.current = null;
          }
        }

        return success;
      },
      [
        companyId,
        getCompanyOrdersByCompanyId,
        handleAllCardsExpandedStateChange,
        ordersDispatchStore.deepLinkedOrder,
        ordersDispatchStore.orderCardsDefaultExpandedState,
        userStore.isCurrentCompanyActive,
      ],
    );

    const handleOrderMutationRefresh = useCallback(
      async (order: Order, action: 'created' | 'updated' | 'canceled') => {
        if (!dispatchSmartAutoRefreshFeatureFlagEnabled) {
          return;
        }

        markOrderAsRecentlyMutated(order.id);

        if (action === 'created') {
          triggerSummaryCountsRefresh();
          return;
        }

        if (action === 'updated') {
          triggerSummaryCountsRefresh();
          return;
        }

        const refreshSucceeded = await requestOrdersRefresh(FetchReason.REFRESH);

        if (!refreshSucceeded) {
          return;
        }

        triggerSummaryCountsRefresh();
      },
      [
        dispatchSmartAutoRefreshFeatureFlagEnabled,
        markOrderAsRecentlyMutated,
        requestOrdersRefresh,
        triggerSummaryCountsRefresh,
      ],
    );

    const handleCancelOrder = useCallback(
      async (orderId: string) => {
        try {
          const cancelledOrder = await cancelOrder(orderId);

          if (cancelledOrder) {
            if (orderFormState.drawer.order?.id === orderId) {
              setOrderFormState((state) => ({
                ...state,
                drawer: { isMinimized: false, isOpen: false },
              }));
            }

            toasterStore.push(
              alert(
                t('dispatch.order.canceled', { name: cancelledOrder.orderId }),
                AlertTypes.success,
              ),
            );

            void handleOrderMutationRefresh(cancelledOrder, 'canceled');
          }
        } catch {
          // Error is handled by the hook
        } finally {
          closeOrderActionDialog();
        }
      },
      [
        cancelOrder,
        closeOrderActionDialog,
        handleOrderMutationRefresh,
        orderFormState.drawer.order?.id,
      ],
    );

    const {
      applyQueuedUpdates,
      dismissBanner,
      handleOrdersFetchCompleted,
      isApplying: isSmartRefreshApplying,
      isBannerVisible,
      isQueueCapped,
      updatesCount,
    } = useDispatchOrdersRtuSmartRefresh({
      companyId,
      hasActiveFilters,
      isAllDatesSelected,
      isFeatureEnabled: Boolean(dispatchSmartAutoRefreshFeatureFlagEnabled),
      isSafeToAutoApply,
      onAfterApplySuccess: triggerSummaryCountsRefresh,
      onRequestRefresh: requestOrdersRefresh,
      recentlyMutatedOrderIdsRef,
    });

    onOrdersFetchCompletedRef.current = handleOrdersFetchCompleted;

    useEffect(() => {
      let ordersRTUSubscription: ReturnType<typeof subscribeToOrdersRTU> | null = null;
      let jobsRTUSubscription: ReturnType<typeof subscribeToJobsRTU> | null = null;
      let bulkAssignJobsRTUSubscription: ReturnType<
        typeof subscribeToBulkAssignJobsRTU
      > | null = null;
      let bulkCopyAssignmentsRTUSubscription: ReturnType<
        typeof subscribeToBulkCopyAssignmentsRTU
      > | null = null;
      let jobUnassignmentRTUSubscription: ReturnType<
        typeof subscribeToJobUnassignmentRTU
      > | null = null;

      if (companyId) {
        ordersRTUSubscription = subscribeToOrdersRTU(companyId);
        jobsRTUSubscription = subscribeToJobsRTU(companyId);
        bulkAssignJobsRTUSubscription = subscribeToBulkAssignJobsRTU(
          companyId,
          handleBulkAssignJobsUpdateReceived,
        );
        bulkCopyAssignmentsRTUSubscription = subscribeToBulkCopyAssignmentsRTU(
          companyId,
          handleBulkCopyAssignmentsUpdateReceived,
        );
        jobUnassignmentRTUSubscription = subscribeToJobUnassignmentRTU(companyId);
      }

      return () => {
        ordersRTUSubscription?.unsubscribe?.();
        jobsRTUSubscription?.unsubscribe?.();
        bulkAssignJobsRTUSubscription?.unsubscribe?.();
        bulkCopyAssignmentsRTUSubscription?.unsubscribe?.();
        jobUnassignmentRTUSubscription?.unsubscribe?.();
      };
    }, [companyId]);

    useEffect(() => {
      if (companyId) {
        setOrdersFetchState({
          ...ordersFetchInitialState,
          isSilentRefresh: false,
          shouldFetch: true,
        });
      }
    }, [companyId]);

    // Self-heal on tab/window return. A project rate-card edit re-rates jobs in a background worker;
    // the live push (CompanyJobUpdateChannel) keeps a mounted+subscribed board fresh, but a push the
    // board missed (order not loaded, tab backgrounded during the edit) would otherwise leave stale
    // rates until a manual reload — the UX we're killing. Silently re-fetch when the board becomes
    // visible/focused again. Routed through requestOrdersRefresh's silent path (never a flashy full
    // reload). Enabled by smart-auto-refresh OR load-level pricing — see selfHealRefreshEnabled.
    useEffect(() => {
      if (!selfHealRefreshEnabled) {
        return undefined;
      }

      const silentlyRefreshOnReturn = () => {
        if (document.visibilityState === 'visible') {
          void requestOrdersRefresh(FetchReason.REFRESH, { silent: true });
        }
      };

      window.addEventListener('focus', silentlyRefreshOnReturn);
      document.addEventListener('visibilitychange', silentlyRefreshOnReturn);

      return () => {
        window.removeEventListener('focus', silentlyRefreshOnReturn);
        document.removeEventListener('visibilitychange', silentlyRefreshOnReturn);
      };
    }, [selfHealRefreshEnabled, requestOrdersRefresh]);

    useEffect(() => {
      if (ordersFetchState.shouldFetch) {
        const fetchKey = JSON.stringify({
          filters: currentFilters,
          isSilentRefresh: ordersFetchState.isSilentRefresh,
          reason: ordersFetchState.reason,
          sort: ordersDispatchStore.ordersSort,
        });

        // Prevent duplicate requests for the exact same fetch intent while one is in-flight.
        if (inFlightOrdersFetchKeyRef.current === fetchKey) {
          return;
        }

        inFlightOrdersFetchKeyRef.current = fetchKey;

        // Preserve the user's bulk selection on a silent self-heal refresh: it is meant to be
        // invisible (refreshing stale rates on tab return), so it must not wipe multi-select the
        // way a user-initiated refresh/filter/sort change does.
        if (
          !ordersFetchState.isSilentRefresh &&
          [
            FetchReason.FILTERS_CHANGED,
            FetchReason.REFRESH,
            FetchReason.ORDER_BY_CHANGED,
          ].includes(ordersFetchState.reason)
        ) {
          bulkActions.clearAllCheckedOrdersAndJobs();
        }

        void performOrdersFetch(
          ordersFetchState.reason,
          ordersFetchState.isSilentRefresh,
        ).finally(() => {
          if (inFlightOrdersFetchKeyRef.current === fetchKey) {
            inFlightOrdersFetchKeyRef.current = null;
          }
        });
      }
    }, [
      bulkActions.clearAllCheckedOrdersAndJobs,
      currentFilters,
      ordersFetchState.isSilentRefresh,
      ordersFetchState.reason,
      ordersFetchState.shouldFetch,
      ordersDispatchStore.ordersSort,
      performOrdersFetch,
    ]);

    useEffect(() => {
      if (!isReadyToFetchOrders) {
        return;
      }

      setOrdersFetchState((state) => {
        if (state.shouldFetch) {
          return state;
        }

        if (!state.initialFetchComplete) {
          return {
            ...state,
            isSilentRefresh: false,
            shouldFetch: true,
            reason: FetchReason.INITIAL_FETCH,
          };
        }

        if (ordersDispatchStore.ordersSort !== previousOrdersSort) {
          return {
            ...state,
            isSilentRefresh: false,
            shouldFetch: true,
            reason: FetchReason.ORDER_BY_CHANGED,
          };
        }

        if (currentFilters !== previousFilters) {
          return {
            ...state,
            isSilentRefresh: false,
            shouldFetch: true,
            reason: FetchReason.FILTERS_CHANGED,
          };
        }

        return { ...state, isSilentRefresh: false, shouldFetch: false };
      });
    }, [
      currentFilters,
      isReadyToFetchOrders,
      ordersDispatchStore.ordersSort,
      previousFilters,
    ]);

    useEffect(() => {
      if (ordersFetchState.initialFetchComplete) {
        ordersDispatchStore.applyPendingDeepLink();
      }
    }, [ordersFetchState.initialFetchComplete]);

    useEffect(() => {
      return () => {
        ordersDispatchStore.clearDeepLink();
        ordersDispatchStore.setFilters({ search: '' }, true);
      };
    }, []);

    useEffect(() => {
      return () => {
        recentMutationTimeoutsRef.current.forEach((timeoutId) => {
          window.clearTimeout(timeoutId);
        });
        recentMutationTimeoutsRef.current.clear();
        recentlyMutatedOrderIdsRef.current.clear();
      };
    }, []);

    const isSidebarCollapsed = ordersDispatchStore.isSidebarCollapsed;

    const driversColumnSx: SxProps = {
      '&.MuiBox-root': {
        borderRight: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        width: `${isSidebarCollapsed ? LEFT_COLUMN_COLLAPSED_WIDTH_IN_PX : JOB_COLUMN_WIDTH}px`,
      },
    };

    const isLargeViewport = useMediaQuery(theme.breakpoints.up('md'));

    // derive sidebar collapsed state from viewport size on first render
    useEffect(() => {
      if (isNil(isSidebarCollapsed)) {
        ordersDispatchStore.toggleSidebar(!isLargeViewport);
      }
    }, [isSidebarCollapsed, isLargeViewport]);

    const dndSensors = useSensors(
      useSensor(NoDnDDataAttributeMouseSensor, {
        activationConstraint: { delay: 50, tolerance: 0 },
      }),
      useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    );

    const handleDragStart = useCallback(
      (...args: Parameters<typeof handleAssigneeDragStart>) => {
        setIsDragging(true);
        handleAssigneeDragStart(...args);
      },
      [handleAssigneeDragStart],
    );

    const handleDragEnd = useCallback(
      (...args: Parameters<typeof handleAssigneeDragEnd>) => {
        setIsDragging(false);
        handleAssigneeDragEnd(...args);
      },
      [handleAssigneeDragEnd],
    );

    const handleDragCancel = useCallback(
      (...args: Parameters<typeof handleAssigneeDragCancel>) => {
        setIsDragging(false);
        handleAssigneeDragCancel(...args);
      },
      [handleAssigneeDragCancel],
    );

    const appliedFiltersNodeRef: MutableRefObject<HTMLDivElement | null> =
      createRef<HTMLDivElement>();
    const [appliedFiltersNodeHeight, setAppliedFiltersNodeHeight] = useState(0);

    useEffect(() => {
      const { height } = appliedFiltersNodeRef.current?.getBoundingClientRect() ?? {};

      setAppliedFiltersNodeHeight(height ?? 0);
    }, [appliedFiltersNodeRef]);

    useEffect(() => {
      if (!isBannerVisible) {
        setUpdatesBannerHeight(0);
        return;
      }

      const { height } = updatesBannerRef.current?.getBoundingClientRect() ?? {};

      setUpdatesBannerHeight(height ?? 0);
    }, [isBannerVisible]);

    useEffect(() => {
      if (!isDateScopeBannerVisible) {
        setDateScopeBannerHeight(0);
        return;
      }

      const { height } = dateScopeBannerRef.current?.getBoundingClientRect() ?? {};

      setDateScopeBannerHeight(height ?? 0);
    }, [isDateScopeBannerVisible]);

    return (
      <>
        <DndContext
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          sensors={dndSensors}
        >
          {!isViewOnlyUser && (
            <Box
              sx={{
                gridRow: 'span 3',
                ...driversColumnSx,
              }}
            >
              <OrdersViewDriverPanel
                ref={driverPanelRef}
                onEditDriver={canEditDrivers ? setDriverToEdit : undefined}
                onEditVendor={canEditVendors ? setVendorToEdit : undefined}
                summaryCountsRefreshToken={summaryCountsRefreshToken}
              />
            </Box>
          )}

          <Box
            sx={{
              alignItems: 'center',
              bgcolor: theme.semantic.surfaceRaised,
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            {viewSelector}

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                ml: 'auto',
              }}
            >
              {simulateOrderFeatureFlagEnabled && <SimulateOrderCTA />}

              <RefreshOrdersListButton
                disabled={ordersDispatchStore.isLoadingOrders}
                onClick={handleRefreshOrdersClick}
                sx={{ ml: 'auto' }}
              />

              {isLlmQuickOrderFeatureFlagOn && (
                <SmallButton
                  color="brandV2Black"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => {
                    // quick_order_opened is tracked by QuickOrderDialog on open
                    setIsQuickOrderDialogOpen(true);
                  }}
                >
                  {t('dispatch.dispatch_v2.quick_order')}
                </SmallButton>
              )}

              <CreateOrderButton
                isForResumeOrderCreation={orderFormState.drawer.isMinimized}
                disabled={userStore.isViewOnlyUser}
                onClick={() => {
                  if (orderFormState.drawer.isMinimized) {
                    setOrderFormState((state) => ({
                      ...state,
                      drawer: {
                        ...state.drawer,
                        isMinimized: false,
                      },
                    }));
                  } else {
                    setOrderFormState((state) => ({
                      ...state,
                      drawer: { isMinimized: false, isOpen: true },
                    }));
                  }
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              bgcolor: theme.semantic.surfaceRaised,
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              display: 'flex',
              gap: 2,
              px: 2,
              py: 1,
            }}
          >
            <SharedDatesFilter
              key={`${ordersDispatchStore.filters.startDate}-${ordersDispatchStore.filters.endDate}`}
              dateRangePickerMode="icon"
              dateRangeUseTodayFormat
              view="orders"
            />

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              {isSingleDaySelected && isBulkSendDayButtonFeatureFlagOn && (
                <SmallButton
                  color="brandV2Green"
                  onClick={() => {
                    AnalyticsController.track('send_all_jobs_opened', { view: 'orders' });
                    setIsSendAllJobsDialogOpen(true);
                  }}
                  startIcon={<Send />}
                  variant="contained"
                >
                  {t('dispatch.dispatch_v2.send_all_jobs')}
                </SmallButton>
              )}

              {canImportOrdersFromCsv && (
                <CSVImportExportButton onClick={csvImportModal.open} />
              )}

              <ExportMenuButton
                columns={dispatchExportColumns}
                disabledTooltip={t('dispatch.export.export_jobs_cta_disabled_tooltip')}
                fetchRows={fetchAllJobsForExport}
                isExportActionDisabled={!isSingleDaySelected}
                footerAction={(closeExportMenu) => (
                  <>
                    <DownloadJobCardPdfsButton
                      isDisabled={!isSingleDaySelected}
                      onRecipientTypeSelect={generateJobCardPdfsForDispatch}
                      placement="exportMenu"
                    />
                    <LoadDigestButton
                      disabledTooltip={t('dispatch.load_digest.range_too_long_tooltip', {
                        maxDays: LOAD_DIGEST_MAX_RANGE_IN_DAYS,
                      })}
                      isDisabled={!isLoadDigestRangeAllowedForFilters}
                      onEmail={() =>
                        setLoadDigestEmailSelection({
                          dispatchSearch: buildDispatchSearchRequest(
                            ordersDispatchStore.filters,
                            { includeDates: true },
                          ),
                        })
                      }
                      onSelect={closeExportMenu}
                      onView={viewForDispatch}
                      placement="exportMenu"
                    />
                  </>
                )}
                label={t('actions.export_jobs')}
                makeFilename={makeExportFilename}
                pdfHeaderMeta={{
                  companyId: userStore.userCompany.id,
                  companyName: userStore.userCompany.legalName,
                  filterDateLabel: dispatchExportFilterDateLabel,
                }}
                pdfTitle={t('actions.export_jobs')}
                surfaceId="dispatch_orders"
              />

              {onPresentationChange ? (
                <Box sx={{ display: 'flex' }}>
                  <ExpandedStatesMenu
                    expandedState={ordersDispatchStore.orderCardsExpandedState.allCards}
                    onExpandedStateChange={handleAllCardsExpandedStateChange}
                    sx={{
                      '&.MuiIconButton-root': {
                        borderBottomRightRadius: 0,
                        borderRight: 0,
                        borderTopRightRadius: 0,
                      },
                    }}
                  />
                  <OrdersPresentationMenu
                    onChange={onPresentationChange}
                    presentation={presentation}
                  />
                </Box>
              ) : (
                <ExpandedStatesMenu
                  expandedState={ordersDispatchStore.orderCardsExpandedState.allCards}
                  onExpandedStateChange={handleAllCardsExpandedStateChange}
                />
              )}

              <OrderBySelector
                onChange={(option) => ordersDispatchStore.setOrdersSort(option)}
                order={ordersDispatchStore.ordersSort}
                orderOptions={sortOptions}
              />
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: theme.semantic.surfaceRaised,
                borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                display: 'flex',
                gap: 1,
                px: 2,
                py: 1,
              }}
            >
              <OrdersStatusFilter
                storageTabKey={OrdersDispatchStorageKeys.TAB_PARAM}
                storeName="ordersDispatchStore"
              />
              <OmniSearchBar />
              <OrdersDispatchFilters />
              {presentation === 'grid' && (
                <Box ref={setColumnsMenuContainer} sx={{ display: 'flex', ml: 'auto' }} />
              )}
            </Box>

            <OrdersViewAppliedFiltersRow innerRef={appliedFiltersNodeRef} />

            {isBannerVisible && (
              <DispatchUpdatesBanner
                ref={updatesBannerRef}
                isApplying={isSmartRefreshApplying}
                isQueueCapped={isQueueCapped}
                onApply={applyQueuedUpdates}
                onDismiss={dismissBanner}
                updatesCount={updatesCount}
              />
            )}

            {autoExtendedBannerVisible ? (
              <DateScopeBanner
                ref={dateScopeBannerRef}
                variant="auto-extended"
                dateLabel={autoExtendedDateLabel}
                onAction={handleReturnToAutoExtendedDates}
              />
            ) : (
              hasResultsOutsideDateRange && (
                <DateScopeBanner
                  ref={dateScopeBannerRef}
                  variant="scoped-to-range"
                  dateLabel={selectedDateRangeLabel}
                  onAction={handleShowResultsFromAllDates}
                />
              )
            )}

            <Box
              sx={{
                height: `calc(100vh - ${HEADER_HEIGHT}px - ${ORDER_DISPATCH_V3_GRID_ROW_HEIGHT_IN_PX}px * 2 - 2px + ${appliedFiltersNodeHeight}px + ${updatesBannerHeight}px + ${dateScopeBannerHeight}px - var(--tread-agent-dock-height, 0px))`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <OrderSearchHighlightProvider searchTerm={orderSearchHighlightTerm}>
                  <OrdersPresentation
                    onLoadMore={() => {
                      if (
                        ordersDispatchStore.hasMore &&
                        !ordersDispatchStore.isLoadingOrders
                      ) {
                        setOrdersFetchState((state) => ({
                          ...state,
                          isSilentRefresh: false,
                          shouldFetch: true,
                          reason: FetchReason.INFINITE_SCROLL,
                        }));
                      }
                    }}
                    checkedOrdersAndJobs={bulkActions.checkedOrdersAndJobs}
                    columnsMenuContainer={columnsMenuContainer}
                    companyHasChildCompanies={hasChildCompanies}
                    onAcceptOrder={handleAcceptOrder}
                    onCheckedStateChange={bulkActions.updateCheckedOrdersAndJobs}
                    onCloneOrderClick={(order) => {
                      // This should be "openOrderActionDialog(OrderActionDialog.CLONE_ORDER, order)", but for
                      // some reason the order passed as argument to "openOrderActionDialog" keeps having old
                      // data after updating the store's orders list via RTU. To avoid this we grab it directly
                      // from the store's orders list.
                      const updatedOrderInList = ordersDispatchStore.orders.find(
                        (storeOrder) => storeOrder.id === order.id,
                      );

                      if (updatedOrderInList) {
                        openOrderActionDialog(
                          OrderActionDialog.CLONE_ORDER,
                          updatedOrderInList,
                        );
                      }
                    }}
                    onCopyAndEditClick={(order) => {
                      dismissCurrentDialogAndOpenDrawerForOrder(order, true);
                    }}
                    onCreateJobFromOrderClick={handleCreateJobFromOrder}
                    onDownloadFilesClick={handleDownloadFilesClick}
                    onEditClick={handleEditOrder}
                    onEmailLoadDigestClick={(order) =>
                      openOrderActionDialog(OrderActionDialog.EMAIL_LOAD_DIGEST, order)
                    }
                    onExpandedStateChange={
                      ordersDispatchStore.toggleOrderCardExpandedState
                    }
                    onOrderStateChange={async (orderId, nextOrderState) => {
                      const order = ordersDispatchStore.orders.find(
                        (o) => o.id === orderId,
                      );
                      if (!order) return Promise.resolve();

                      let updatedOrder: Order | undefined;

                      switch (nextOrderState) {
                        case OrderState.CANCELED:
                          openOrderActionDialog(OrderActionDialog.CANCEL_ORDER, order);
                          break;
                        case OrderState.COMPLETED:
                          void handleCompleteOrder(order.id);
                          break;
                        case OrderState.REJECTED:
                          openOrderActionDialog(OrderActionDialog.REJECT_ORDER, order);
                          break;
                        case OrderState.ACCEPTED:
                          updatedOrder = await acceptOrderEvent(order.id);
                          break;
                        case OrderState.IN_PROGRESS:
                          updatedOrder = await startOrder(order.id);
                          break;
                        case OrderState.PENDING_REQUEST:
                          updatedOrder = await requestOrder(order.id);
                          break;
                        case OrderState.HOLD:
                          updatedOrder = await holdOrder(order.id);
                          break;
                        case OrderState.WEATHER_HOLD:
                          updatedOrder = await weatherHoldOrder(order.id);
                          break;
                      }

                      if (updatedOrder) {
                        ordersDispatchStore.upsertOrder(updatedOrder);
                      }
                    }}
                    onRejectOrder={(order) => {
                      openOrderActionDialog(OrderActionDialog.REJECT_ORDER, order);
                    }}
                    onShowAllDates={handleShowResultsFromAllDates}
                    onTextAllDriversClick={(order) => {
                      openOrderActionDialog(OrderActionDialog.TEXT_ALL_DRIVERS, order);
                    }}
                    onBulkAssignMultipleClick={(order) => {
                      openOrderActionDialog(
                        OrderActionDialog.BULK_ASSIGN_MULTIPLE,
                        order,
                      );
                    }}
                    selectedDateRangeLabel={selectedDateRangeLabel}
                  />
                </OrderSearchHighlightProvider>
              </Box>
            </Box>
          </Box>

          <DragOverlay>
            {ordersDispatchStore.draggedAssignee &&
            ordersDispatchStore.draggedAssigneeTruckCapacity ? (
              <Box sx={{ px: 1.5, width: `${JOB_COLUMN_WIDTH}px` }}>
                <DriverPanelVendorTypeSubCard
                  isAvailable
                  placement={DriverPanelAssigneeCardPlacement.DRAGGING}
                  equipmentTypeStat={ordersDispatchStore.draggedAssigneeTruckCapacity}
                  vendorId={ordersDispatchStore.draggedAssignee.id}
                />
              </Box>
            ) : ordersDispatchStore.draggedAssignee ? (
              <DriverPanelAssigneeCard
                assignee={ordersDispatchStore.draggedAssignee}
                placement={DriverPanelAssigneeCardPlacement.DRAGGING}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        {isBulkActionsBarVisible && (
          <OrdersViewBulkActionsBar
            jobsCount={bulkActions.jobsCount}
            onClearAll={bulkActions.clearAllCheckedOrdersAndJobs}
          >
            <BulkActionsBarButton
              color="inherit"
              disabled={bulkActions.copyableJobs.length === 0}
              onClick={() => {
                AnalyticsController.track('order_copy_opened', {
                  job_count: bulkActions.copyableJobs.length,
                });
                bulkActions.openDialog('copyVendorAssignments');
              }}
              startIcon={<ContentCopyOutlined />}
            >
              {t('actions.copy_to')}
            </BulkActionsBarButton>

            <BulkActionsBarButton
              color="inherit"
              disabled={
                bulkActions.jobsWithEditableNotes.uniqueOrdersHavingCheckedJobs.size !== 1
              }
              onClick={() => {
                AnalyticsController.track('jobs_bulk_edit_opened', {
                  job_count: bulkActions.jobsWithEditableNotes.jobIds.length,
                });
                bulkActions.openDialog('editJobNotes');
              }}
              startIcon={<EditOutlined />}
            >
              {t('dispatch.order.edit')}
            </BulkActionsBarButton>

            <BulkActionsBarButton
              color="inherit"
              disabled={bulkActions.cancelableJobs.length === 0}
              onClick={() => bulkActions.openDialog('cancel')}
              startIcon={<CancelOutlined />}
            >
              {t('actions.cancel')}
            </BulkActionsBarButton>

            <BulkActionsBarButton
              color="inherit"
              disabled={bulkActions.jobsWithTextableDrivers.length === 0}
              onClick={() => bulkActions.openDialog('textAllDrivers')}
              startIcon={<SmsOutlined />}
            >
              {t('actions.text')}
            </BulkActionsBarButton>

            <Tooltip title={t('dispatch.order.assign_to_vendor_tooltip')}>
              <Box sx={{ display: 'inline-block' }}>
                <BulkActionsBarButton
                  color="inherit"
                  disabled={bulkActions.assignableJobs.length === 0}
                  onClick={() => bulkActions.openDialog('assign')}
                  startIcon={<PersonAddOutlined />}
                >
                  {t('dispatch.order.assign_to_vendor')}
                </BulkActionsBarButton>
              </Box>
            </Tooltip>

            <BulkActionsBarButton
              color="inherit"
              disabled={bulkActions.unassignableJobs.length === 0}
              onClick={async () => {
                const jobIds = bulkActions.unassignableJobs;
                if (!jobIds.length) return;

                bulkActions.clearAllCheckedOrdersAndJobs();
                try {
                  await bulkUnassignJobs(jobIds, companyId);
                  trackJobAssignmentCancelled({
                    action: JobStateEvent.UNASSIGN,
                    jobIds,
                    source: 'bulk_actions',
                  });
                } catch {
                  // Error is handled by the hook
                }
              }}
              startIcon={<ClearOutlined />}
            >
              {t('actions.unassign')}
            </BulkActionsBarButton>

            <DownloadJobCardPdfsButton
              onRecipientTypeSelect={generateJobCardPdfsForCheckedJobs}
              placement="bulkActionsBar"
            />

            <LoadDigestButton
              onEmail={() => setLoadDigestEmailSelection({ jobIds: checkedJobIds })}
              onView={viewLoadDigestForCheckedJobs}
              placement="bulkActionsBar"
            />

            <BulkActionsBarButton
              color="brandV2Green"
              disabled={bulkActions.sendableJobs.length === 0 || isSendingSelectedJobs}
              onClick={handleSendSelectedJobs}
              startIcon={<SendOutlined />}
            >
              {t('actions.send')}
            </BulkActionsBarButton>
          </OrdersViewBulkActionsBar>
        )}
        <OrderForm
          defaultDate={ordersDispatchStore.filters.startDate}
          isCopyAndEdit={orderFormState.drawer.isCopyAndEdit}
          isMinimized={orderFormState.drawer.isMinimized}
          isOpen={orderFormState.drawer.isOpen}
          keepMounted={orderFormState.drawer.keepMounted}
          onClose={() => {
            setOrderFormState(() => ({
              drawer: { isMinimized: false, isOpen: false },
              confirmationDialog: { isOpen: false },
            }));
          }}
          onMinimize={() => {
            setOrderFormState((state) => ({
              ...state,
              drawer: { ...state.drawer, isMinimized: true, keepMounted: true },
            }));
          }}
          onOrderSaved={(order, action) => {
            void handleOrderMutationRefresh(order, action);
          }}
          order={orderFormState.drawer.order}
        />
        <ConfirmationDialog
          confirmButtonProps={{ color: 'primary' }}
          isOpen={orderFormState.confirmationDialog.isOpen}
          onClose={() => {
            setOrderFormState((state) => ({
              ...state,
              confirmationDialog: { ...state.confirmationDialog, isOpen: false },
            }));
          }}
          onConfirm={() => {
            const orderToOpen = orderFormState.confirmationDialog.order;

            setOrderFormState((state) => ({
              ...state,
              confirmationDialog: { ...state.confirmationDialog, isOpen: false },
            }));

            if (orderToOpen) {
              dismissCurrentDialogAndOpenDrawerForOrder(orderToOpen);
            }
          }}
          title={t('dispatch.order.edit_order')}
        >
          <Typography>
            {t('dispatch.dispatch_v2.edit_confirmation_dialog_text', {
              action: orderFormState.drawer.order?.id
                ? t('common.edited').toLowerCase()
                : t('common.created').toLowerCase(),
              orderId: orderFormState.confirmationDialog.order?.orderId,
            })}
          </Typography>
        </ConfirmationDialog>
        <SendAllJobsForDayDialog
          fetchJobsCount={fetchSendAllJobsCount}
          isOpen={isSendAllJobsDialogOpen}
          onClose={() => setIsSendAllJobsDialogOpen(false)}
          onConfirm={handleBulkSendJobsForDay}
        />
        <BulkAssignJobsDialog
          accountId={bulkActions.dialogsState.assign.selectedVendorAccountId ?? null}
          companyId={companyId}
          filterDate={ordersDispatchStore.filters.startDate}
          isOpen={bulkActions.dialogsState.assign.isOpen}
          onClose={() => bulkActions.closeDialog('assign')}
          onConfirm={(complianceOverride?: boolean) => {
            const { assignableJobs, dialogsState } = bulkActions;
            const { selectedVendorAccountId } = dialogsState.assign;

            if (selectedVendorAccountId && assignableJobs.length) {
              void bulkAssignJobs(
                assignableJobs,
                selectedVendorAccountId,
                companyId,
                complianceOverride,
              ).catch(() => {
                // Error is handled by the hook
              });
            }

            bulkActions.closeDialog('assign');
          }}
          onSelectedAccountChange={bulkActions.updateBulkAssignJobsSelectedAccount}
        />
        <BulkAssignMultipleJobsDialog
          filterDate={ordersDispatchStore.filters.startDate}
          isOpen={
            orderActionDialog.isOpen &&
            orderActionDialog.dialog === OrderActionDialog.BULK_ASSIGN_MULTIPLE
          }
          onClose={closeOrderActionDialog}
          onConfirm={closeOrderActionDialog}
          order={orderActionDialog.order}
        />
        {pendingTypeMismatchAssignment && (
          <AssignTypeMismatchDialog
            equipmentTypeName={pendingTypeMismatchAssignment.equipmentTypeName}
            isOpen
            isSubmitting={isTypeMismatchSubmitting}
            onCancel={cancelTypeMismatchAssignment}
            onConfirm={confirmTypeMismatchAssignment}
            requestedEquipmentTypeName={
              pendingTypeMismatchAssignment.requestedEquipmentTypeName
            }
            vendorName={pendingTypeMismatchAssignment.vendorName}
          />
        )}
        {pendingDragAssignment && (
          <ComplianceWarningDialog
            isOpen={!!pendingDragAssignment}
            result={pendingDragAssignment.complianceResult}
            isSubmitting={isDragOverrideSubmitting}
            onCancel={cancelDragAssignment}
            onOverride={confirmDragAssignment}
          />
        )}
        <BulkEditJobDialog
          defaultValues={{
            notes: bulkActions.jobsWithEditableNotes.defaultNotes ?? undefined,
          }}
          isOpen={bulkActions.dialogsState.editJobNotes.isOpen}
          jobIds={bulkActions.jobsWithEditableNotes.jobIds}
          onClose={() => bulkActions.closeDialog('editJobNotes')}
          orderId={materialsOrderId}
          shouldLockNonEditableFields={shouldLockNonEditableFieldsInBulkEditJobsDialog}
        />
        <QuickOrderDialog
          isOpen={isQuickOrderDialogOpen}
          onClose={() => {
            setIsQuickOrderDialogOpen(false);
          }}
        />
        {canImportOrdersFromCsv && (
          <CSVImportExportModal
            companyId={companyId}
            config={ordersImportConfig}
            entityDisplayName={t('page_headings.orders')}
            isOpen={csvImportModal.isOpen}
            onClose={csvImportModal.close}
            onImportComplete={csvImportModal.handleImportComplete}
          />
        )}
        <CopyVendorAssignmentsDialog
          companyId={companyId}
          isOpen={bulkActions.dialogsState.copyVendorAssignments.isOpen}
          orderId={bulkActions.dialogsState.copyVendorAssignments.selectedOrderId}
          onClose={() => bulkActions.closeDialog('copyVendorAssignments')}
          onConfirm={() => {
            const { copyableJobs, dialogsState } = bulkActions;
            const { selectedOrderId } = dialogsState.copyVendorAssignments;

            if (selectedOrderId && copyableJobs.length) {
              AnalyticsController.track('order_copied', {
                source_job_count: copyableJobs.length,
                target_order_id: selectedOrderId,
                target_order_friendly_id:
                  ordersDispatchStore.orders.find((o) => o.id === selectedOrderId)
                    ?.orderId ?? '',
              });
              void bulkCopyAssignments(copyableJobs, selectedOrderId, companyId).catch(
                () => {
                  // Error is handled by the hook
                },
              );
            }

            bulkActions.closeDialog('copyVendorAssignments');
          }}
          onSelectedOrderChange={bulkActions.updateBulkCopyAssignmentsSelectedOrder}
        />
        {orderActionDialog.order && (
          <CloneOrderDialog
            isOpen={
              orderActionDialog.isOpen &&
              orderActionDialog.dialog === OrderActionDialog.CLONE_ORDER
            }
            onClose={closeOrderActionDialog}
            onOrderCloned={handleOrderCloned}
            order={orderActionDialog.order}
          />
        )}
        <SendTextDialog
          isOpen={
            bulkActions.dialogsState.textAllDrivers.isOpen ||
            (orderActionDialog.isOpen &&
              orderActionDialog.dialog === OrderActionDialog.TEXT_ALL_DRIVERS)
          }
          onClose={closeOrderActionDialog}
          mode={
            bulkActions.dialogsState.textAllDrivers.isOpen
              ? 'bulk_drivers'
              : 'order_drivers'
          }
          source={
            bulkActions.dialogsState.textAllDrivers.isOpen
              ? bulkActions.jobsWithTextableDrivers
              : orderActionDialog.order
          }
        />
        <ConfirmationDialog
          isBusy={ordersDispatchStore.isCancellingOrder}
          isOpen={
            orderActionDialog.isOpen &&
            orderActionDialog.dialog === OrderActionDialog.CANCEL_ORDER
          }
          onClose={closeOrderActionDialog}
          onConfirm={() => {
            if (orderActionDialog.order?.id) {
              handleCancelOrder(orderActionDialog.order.id);
            }
          }}
          title={t('dispatch.order.cancel_order')}
        >
          <Typography>{t('dispatch.order.cancel_description')}</Typography>
        </ConfirmationDialog>
        <ConfirmationDialog
          isOpen={bulkActions.dialogsState.cancel.isOpen}
          onClose={() => bulkActions.closeDialog('cancel')}
          onConfirm={async () => {
            const jobIds = bulkActions.cancelableJobs;
            bulkActions.closeDialog('cancel');
            if (!jobIds.length) return;

            bulkActions.clearAllCheckedOrdersAndJobs();
            try {
              await bulkCancelJobs(jobIds, companyId);
              trackJobAssignmentCancelled({
                action: JobStateEvent.CANCEL,
                jobIds,
                source: 'bulk_actions',
              });
            } catch {
              // Error is handled by the hook
            }
          }}
          title={t('dispatch.job.bulk_cancel_jobs_dialog_title')}
        >
          <Typography>
            {t('dispatch.job.bulk_cancel_jobs_dialog_text', {
              count: bulkActions.cancelableJobs.length,
            })}
          </Typography>
        </ConfirmationDialog>
        <RejectOrderDialog
          isOpen={
            orderActionDialog.isOpen &&
            orderActionDialog.dialog === OrderActionDialog.REJECT_ORDER
          }
          onClose={closeOrderActionDialog}
          order={orderActionDialog.order}
        />
        {orderActionDialog.order && (
          <EmailLoadDigestDialog
            isOpen={
              orderActionDialog.isOpen &&
              orderActionDialog.dialog === OrderActionDialog.EMAIL_LOAD_DIGEST
            }
            onClose={closeOrderActionDialog}
            selection={{ orderId: orderActionDialog.order.id }}
          />
        )}
        {loadDigestEmailSelection && (
          <EmailLoadDigestDialog
            isOpen
            onClose={() => setLoadDigestEmailSelection(null)}
            selection={loadDigestEmailSelection}
          />
        )}
        <DownloadFilesDialog
          isOpen={downloadFilesDialogState.isOpen}
          jobIds={downloadFilesDialogState.jobIds}
          onClose={() => setDownloadFilesDialogState({ isOpen: false, jobIds: [] })}
        />
        {canEditDrivers && (
          <EditDriverDrawer
            driver={driverToEdit}
            onClose={(updatedDriver) => {
              setDriverToEdit(undefined);

              if (updatedDriver) {
                ordersDispatchStore.resetOrders(ordersDispatchStore.filters);
                driverPanelRef.current?.refetch();
                toasterStore.push(
                  alert(
                    t('user.user_updated', { name: updatedDriver.fullName }),
                    AlertTypes.success,
                  ),
                );
              }
            }}
          />
        )}
        {canEditVendors && (
          <EditVendorDrawer
            account={vendorToEdit}
            onClose={(updatedAccount) => {
              setVendorToEdit(undefined);

              if (updatedAccount) {
                ordersDispatchStore.resetOrders(ordersDispatchStore.filters);
                driverPanelRef.current?.refetch();
                toasterStore.push(
                  alert(
                    t('account.account_updated', { name: updatedAccount.name }),
                    AlertTypes.success,
                  ),
                );
              }
            }}
          />
        )}
      </>
    );
  },
);

export default OrdersViewProvider;
